import React, { useCallback, useEffect, useState } from 'react';
import { CheckCheck } from 'lucide-react';
import { api } from '../services/api';
import { ALERT_KIND, goToAlert, timeAgo } from './AdminNotificationsBell';

const KINDS = ['', 'order', 'contact', 'fitting', 'cron', 'review', 'stock', 'seo', 'email'];

// Admin › Alertes & tâches : full history of admin alerts with kind / unread filters, read toggles and pagination
const AdminAlertsHistory = ({ onNavigate }) => {
  const [kind, setKind] = useState('');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get('/admin/alerts', { params: { kind, unread: unreadOnly, page, limit: 15 } }).then((r) => setData(r.data)).catch(() => {}), [kind, unreadOnly, page]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [kind, unreadOnly]);
  const toggle = async (a) => { await api.put(`/admin/alerts/${a.id}`, { read: !a.read }).catch(() => {}); load(); };
  const readAll = async () => { await api.post('/admin/alerts/read-all').catch(() => {}); load(); };
  return (
    <div className="bg-white rounded-xl border overflow-hidden" data-testid="admin-alerts-history">
      <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b bg-gray-50">
        <span className="text-sm font-semibold text-gray-800">Historique des alertes {data && <span className="text-xs font-normal text-gray-500">({data.total} · {data.unread} non lue{data.unread > 1 ? 's' : ''})</span>}</span>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <select value={kind} onChange={(e) => setKind(e.target.value)} className="h-8 text-sm rounded-md border border-gray-300 bg-white px-2" data-testid="alerts-filter-kind">
            {KINDS.map((k) => <option key={k} value={k}>{k ? `${ALERT_KIND[k].label} (${data?.counts?.[k] ?? 0})` : 'Tous les types'}</option>)}
          </select>
          <label className="text-xs text-gray-700 flex items-center gap-1"><input type="checkbox" checked={unreadOnly} onChange={(e) => setUnreadOnly(e.target.checked)} data-testid="alerts-unread-only" /> Non lues</label>
          <button type="button" onClick={readAll} disabled={!data?.unread} className="text-xs text-orange-600 disabled:text-gray-400 inline-flex items-center gap-1" data-testid="alerts-read-all"><CheckCheck size={13} /> Tout marquer lu</button>
        </div>
      </div>
      {data && data.items.length === 0 && <p className="p-6 text-sm text-gray-400 text-center" data-testid="alerts-empty">Aucune alerte.</p>}
      {data && data.items.length > 0 && (
        <ul className="divide-y" data-testid="alerts-list">
          {data.items.map((a) => { const k = ALERT_KIND[a.kind] || ALERT_KIND.cron; return (
            <li key={a.id} className={`flex items-start gap-3 px-4 py-2.5 text-sm ${a.read ? '' : 'bg-orange-50/50'}`} data-testid={`alert-row-${a.id}`}>
              <span className={`mt-0.5 w-8 h-8 shrink-0 rounded-lg flex items-center justify-center ${k.cls}`}><k.icon size={15} /></span>
              <div className="flex-1 min-w-0">
                <button type="button" onClick={() => goToAlert(a, onNavigate)} className={`text-left hover:text-orange-600 ${a.read ? 'text-gray-700' : 'font-semibold text-gray-900'}`} data-testid={`alert-open-${a.id}`}>{a.title}</button>
                <p className="text-xs text-gray-500">{a.body}</p>
                <p className="text-[11px] text-gray-400 mt-0.5">{k.label} · {new Date(a.created_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })} · {timeAgo(a.created_at)}</p>
              </div>
              <button type="button" onClick={() => toggle(a)} className="text-xs text-gray-500 hover:text-gray-800 shrink-0" data-testid={`alert-toggle-${a.id}`}>{a.read ? 'Marquer non lu' : 'Marquer lu'}</button>
            </li>
          ); })}
        </ul>
      )}
      {data && data.pages > 1 && (
        <div className="flex items-center justify-between px-3 py-2 border-t text-sm">
          <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="px-3 py-1 rounded border disabled:opacity-40" data-testid="alerts-prev">Précédent</button>
          <span className="text-gray-500">Page {page} / {data.pages}</span>
          <button type="button" disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)} className="px-3 py-1 rounded border disabled:opacity-40" data-testid="alerts-next">Suivant</button>
        </div>
      )}
    </div>
  );
};

export default AdminAlertsHistory;
